// B1.4: move-only from client.ts.
import { ApiClientCore, ApiError, buildQueryString } from './http/core';

export class CounterpartiesApi extends ApiClientCore {
  async archiveCounterpartyTemplate(templateId: number): Promise<any> {
    return this.post(`/api/v1/counterparties/templates/${templateId}/archive`);
  }
  async createCounterparty(data: any): Promise<any> {
    return this.post('/api/v1/consumables/counterparties', data);
  }
  async createCounterpartyAccount(data: {
    location_id: number;
    template_id: number;
    name: string;
  }): Promise<any> {
    return this.post('/api/v1/counterparties/accounts', data);
  }
  async createCounterpartyAdjustment(
    accountId: number,
    data: { supplier_id: number; amount: number; comment?: string; reconciliation_id?: number },
  ): Promise<any> {
    return this.post(`/api/v1/counterparties/accounts/${accountId}/adjustments`, data);
  }
  async createCounterpartyDefaultTemplate(): Promise<{ template_id: number; created: boolean }> {
    return this.post('/api/v1/counterparties/templates/default-preset');
  }
  async createCounterpartyReconciliation(
    accountId: number,
    data: { supplier_id: number; actual_amount: number; comment?: string; photo_file_id?: number },
  ): Promise<{
    id: number;
    system_balance: number;
    actual_amount: number;
    delta: number;
    has_mismatch: boolean;
  }> {
    return this.post(`/api/v1/counterparties/accounts/${accountId}/reconciliations`, data);
  }
  async createCounterpartySupplier(data: Record<string, unknown>): Promise<any> {
    return this.post('/api/v1/counterparties/suppliers', data);
  }
  async deleteArchivedCounterpartyAccount(accountId: number): Promise<{
    account_id: number;
    deleted: boolean;
    operation_count: number;
    adjustment_count: number;
  }> {
    return this.delete(`/api/v1/counterparties/accounts/${accountId}`);
  }
  async deleteArchivedCounterpartyTemplate(templateId: number): Promise<{
    template_id: number;
    deleted: boolean;
    operation_count: number;
    archived_account_count: number;
  }> {
    return this.delete(`/api/v1/counterparties/templates/${templateId}`);
  }
  async deleteCounterparty(id: number): Promise<any> {
    return this.delete(`/api/v1/consumables/counterparties/${id}`);
  }
  async deleteCounterpartySupplier(id: number): Promise<void> {
    return this.delete(`/api/v1/counterparties/suppliers/${id}`);
  }
  async downloadCounterpartyAccountQrPrintPdf(accountIds: number[]): Promise<void> {
    if (accountIds.length !== 1) {
      throw new Error('Печать одного счёта за раз');
    }
    const { parseContentDispositionFilename } = await import('@/lib/parse-content-disposition');
    const qs = encodeURIComponent(String(accountIds[0]));
    const url = `${this.baseURL}/api/v1/counterparties/accounts/qr/print.pdf?account_ids=${qs}`;
    const response = await this.authedFetch(url, { method: 'GET' });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        response.status,
        (errorData as any).detail || (errorData as any).error || response.statusText,
        errorData,
      );
    }
    const filename =
      parseContentDispositionFilename(response.headers.get('Content-Disposition')) ||
      'QR наклейка.pdf';
    const blob = await response.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  }
  async getCounterparties(params?: { is_active?: boolean }): Promise<any[]> {
    return this.get(`/api/v1/consumables/counterparties${buildQueryString(params || {})}`);
  }
  async getCounterpartyAccountLedger(
    accountId: number,
    params: Record<string, unknown> = {},
  ): Promise<{ balance?: Record<string, unknown>; entries: any[] }> {
    return this.get(`/api/v1/counterparties/accounts/${accountId}/ledger${buildQueryString(params)}`);
  }
  async getCounterpartyAccountLedgerAll(
    accountId: number,
    params: Record<string, unknown> = {},
  ): Promise<{ balance?: Record<string, unknown>; entries: any[] }> {
    const pageSize = 200;
    let balance: Record<string, unknown> | undefined;
    const allEntries: any[] = [];
    let offset = 0;
    for (;;) {
      const res = await this.getCounterpartyAccountLedger(accountId, {
        ...params,
        limit: pageSize,
        offset,
      });
      if (res.balance) balance = res.balance;
      const entries = Array.isArray(res.entries) ? res.entries : [];
      allEntries.push(...entries);
      if (entries.length < pageSize) break;
      offset += pageSize;
      if (offset > 10_000) break;
    }
    return { balance, entries: allEntries };
  }
  async getCounterpartyAccountQr(id: number): Promise<any> {
    return this.get(`/api/v1/counterparties/accounts/${id}/qr`);
  }
  async getCounterpartyAccountUsage(accountId: number): Promise<{
    account_id: number;
    account_name: string;
    is_active: boolean;
    operation_count: number;
    adjustment_count: number;
  }> {
    return this.get(`/api/v1/counterparties/accounts/${accountId}/usage`);
  }
  async getCounterpartyAccounts(params: Record<string, unknown> = {}): Promise<any[]> {
    return this.get(`/api/v1/counterparties/accounts${buildQueryString(params)}`);
  }
  async getCounterpartyHistory(params: Record<string, unknown> = {}): Promise<any[]> {
    return this.get(`/api/v1/counterparties/history${buildQueryString(params)}`);
  }
  async getCounterpartyHistoryAll(params: Record<string, unknown> = {}): Promise<any[]> {
    const pageSize = 200;
    const all: any[] = [];
    let offset = 0;
    for (;;) {
      const batch = await this.getCounterpartyHistory({ ...params, limit: pageSize, offset });
      const rows = Array.isArray(batch) ? batch : [];
      all.push(...rows);
      if (rows.length < pageSize) break;
      offset += pageSize;
      if (offset > 10_000) break;
    }
    return all;
  }
  async getCounterpartySummary(params: Record<string, unknown> = {}): Promise<any> {
    return this.get(`/api/v1/counterparties/summary${buildQueryString(params)}`);
  }
  async getCounterpartySupplierOpeningBalance(supplierId: number): Promise<any> {
    return this.get(`/api/v1/counterparties/suppliers/${supplierId}/opening-balance`);
  }
  async getCounterpartySuppliers(params?: { is_active?: boolean }): Promise<any[]> {
    return this.get(`/api/v1/counterparties/suppliers${buildQueryString(params || {})}`);
  }
  async getCounterpartyTemplateCalcRoles(templateId: number): Promise<any> {
    return this.get(`/api/v1/counterparties/templates/${templateId}/calc-roles`);
  }
  async getCounterpartyTemplateUsage(templateId: number): Promise<{
    template_id: number;
    account_count: number;
    account_names: string[];
    operation_count: number;
    last_used_at?: string | null;
    can_delete: boolean;
  }> {
    return this.get(`/api/v1/counterparties/templates/${templateId}/usage`);
  }
  async openCounterpartyManual(accountId: number): Promise<{
    checklist_id: number;
    account_id: number;
    account_name?: string;
    template_id: number;
    template_name?: string;
    location_id: number;
    location_name?: string;
    deep_link_path?: string;
    created: boolean;
  }> {
    return this.post(`/api/v1/counterparties/accounts/${accountId}/open-manual`, {});
  }
  async putCounterpartyTemplateCalcRoles(
    templateId: number,
    items: Array<{
      template_item_id: number;
      calc_role: string;
      sign?: number;
      custom_label?: string;
    }>,
  ): Promise<any> {
    return this.put(`/api/v1/counterparties/templates/${templateId}/calc-roles`, { items });
  }
  async regenerateCounterpartyAccountQr(id: number): Promise<any> {
    return this.post(`/api/v1/counterparties/accounts/${id}/qr/regenerate`);
  }
  async restoreCounterpartyTemplate(templateId: number): Promise<any> {
    return this.post(`/api/v1/counterparties/templates/${templateId}/restore`);
  }
  async setCounterpartySupplierOpeningBalance(
    supplierId: number,
    data: { total_amount: number; lines: Array<{ location_id: number; amount: number }> },
  ): Promise<any> {
    return this.put(`/api/v1/counterparties/suppliers/${supplierId}/opening-balance`, data);
  }
  async updateCounterparty(id: number, data: any): Promise<any> {
    return this.put(`/api/v1/consumables/counterparties/${id}`, data);
  }
  async updateCounterpartyAccount(
    id: number,
    data: {
      name?: string;
      template_id?: number;
      is_active?: boolean;
      recount_enabled?: boolean;
      recount_interval_hours?: number | null;
    },
  ): Promise<any> {
    return this.patch(`/api/v1/counterparties/accounts/${id}`, data);
  }
  async updateCounterpartySupplier(id: number, data: Record<string, unknown>): Promise<any> {
    return this.put(`/api/v1/counterparties/suppliers/${id}`, data);
  }
}
